export type StyleKey = 'realistic' | 'anime';
export type GenderKey = 'female' | 'trans';

export type QuizOption = {
  id: string;
  label: string;
  image?: string;
  imageByStyle?: Partial<Record<StyleKey, string>>;
  imageByGenderStyle?: Partial<Record<GenderKey, Partial<Record<StyleKey, string>>>>;
  video?: string;
  videoByGender?: Partial<Record<GenderKey, string>>;
  description?: string;
};

const asset = (path: string) => `/vxlover/quiz/${path}`;

export const styleOptions: QuizOption[] = [
  {
    id: 'realistic',
    label: 'Realistic',
    image: asset('images/app-create/style-step/realistic-nsfw.jpg'),
    video: asset('videos/app-create/style-step/realistic-nsfw.webm'),
    videoByGender: {
      female: asset('videos/app-create/style-step/realistic-nsfw.webm'),
      trans: asset('videos/app-create/style-step/realistic-tgirl.webm')
    },
    description: 'Photorealistic companion with natural skin, soft light, and lifelike details.'
  },
  {
    id: 'anime',
    label: 'Anime',
    image: asset('images/app-create/style-step/anime-nsfw.jpg'),
    video: asset('videos/app-create/style-step/anime-nsfw.webm'),
    videoByGender: {
      female: asset('videos/app-create/style-step/anime-nsfw.webm'),
      trans: asset('videos/app-create/style-step/anime-tgirl.webm')
    },
    description: 'Expressive fantasy style with glowing color, polished faces, and dramatic poses.'
  }
];

export const genderOptions: QuizOption[] = [
  { id: 'female', label: 'Female' },
  { id: 'trans', label: 'Trans' }
];

export const ethnicityOptions: QuizOption[] = ['Asian', 'Black', 'White', 'Latina', 'Arab', 'Indian', 'Elf', 'Demon'].map((label) => {
  const id = label.toLowerCase();
  return {
    id,
    label,
    image: asset(`images/app-create/ethnicity-step/${id}-nsfw.avif`),
    imageByStyle: {
      realistic: asset(`images/app-create/ethnicity-step/${id}-nsfw.avif`),
      anime: asset(`images/app-create/ethnicity-step/${id}-anime-nsfw.avif`)
    }
  };
});

export const eyeColorOptions = ['Brown', 'Blue', 'Green', 'Hazel', 'Gray', 'Black', 'Amber', 'Gold', 'Red', 'Violet'].map((label) => {
  const id = label.toLowerCase();
  const colors: Record<string, string> = {
    brown: '#6b3f26',
    blue: '#4f8cff',
    green: '#5fbf72',
    hazel: '#9a7436',
    gray: '#9a9ca5',
    black: '#141218',
    amber: '#c77722',
    gold: '#d8a52f',
    red: '#bb2d40',
    violet: '#8a54ff'
  };
  return {
    id,
    label,
    color: colors[id],
    image: asset(`images/app-create/face/eye-color/${id}.avif`),
    imageByStyle: {
      realistic: asset(`images/app-create/face/eye-color/${id}.avif`),
      anime: asset(`images/app-create/face/eye-color/${id}-anime.avif`)
    }
  };
});

const styleImageOption = (basePath: string, label: string, animeFileId = label.toLowerCase()): QuizOption => {
  const id = label.toLowerCase();
  return {
    id,
    label,
    image: asset(`${basePath}/${id}-nsfw.avif`),
    imageByStyle: {
      realistic: asset(`${basePath}/${id}-nsfw.avif`),
      anime: asset(`${basePath}/${animeFileId}-anime-nsfw.avif`)
    }
  };
};

const bodyTypeOption = (label: string): QuizOption => {
  const id = label.toLowerCase();
  return {
    id,
    label,
    image: asset(`images/app-create/body-type/${id}-nsfw.avif`),
    imageByStyle: {
      realistic: asset(`images/app-create/body-type/${id}-nsfw.avif`),
      anime: asset(`images/app-create/body-type/${id}-anime-nsfw.avif`)
    },
    imageByGenderStyle: {
      trans: {
        realistic: asset(`images/app-create/body-type/${id}-tgirl.avif`),
        anime: asset(`images/app-create/body-type/${id}-anime-tgirl.avif`)
      }
    }
  };
};

export const hairColorOptions: QuizOption[] = [
  ['Brown'],
  ['Black'],
  ['Blonde'],
  ['Red'],
  ['Grey', 'gray'],
  ['Hazel'],
  ['Amber'],
  ['Gold'],
  ['Blue'],
  ['Green'],
  ['Violet']
].map(([label, animeFileId]) => styleImageOption('images/app-create/face/hair-color', label, animeFileId));

export const hairStyleOptions: QuizOption[] = ['Braided', 'Long', 'Bangs', 'Ponytail', 'Short', 'Bun', 'Buns', 'Wavy'].map((label) =>
  styleImageOption('images/app-create/face/hair-style', label)
);

export const bodyTypeOptions: QuizOption[] = ['Slim', 'Athletic', 'Voluptuous', 'Curvy', 'Muscular'].map((label) => bodyTypeOption(label));

export const breastSizeOptions: QuizOption[] = ['Flat', 'Small', 'Medium', 'Large', 'XL'].map((label) =>
  styleImageOption('images/app-create/breast-size', label)
);

export const buttSizeOptions: QuizOption[] = ['Small', 'Skinny', 'Athletic', 'Medium', 'Large'].map((label) =>
  styleImageOption('images/app-create/butt-size', label)
);

export const detailCards = [
  { key: 'voice', label: 'Voice', value: 'Playful', image: asset('images/app-create/personality/voice.avif') },
  { key: 'personality', label: 'Personality', value: 'Flirty', image: asset('images/app-create/personality/personality.avif') },
  { key: 'occupation', label: 'Occupation', value: 'Dancer', image: asset('images/app-create/personality/occupation.avif') },
  { key: 'relationship', label: 'Relationship', value: 'Crush', image: asset('images/app-create/personality/relationship.avif') },
  { key: 'hobby', label: 'Hobby', value: 'Dancing', image: asset('images/app-create/personality/hobby.avif') },
  { key: 'fantasy', label: 'Fantasy', value: 'Roleplay', image: asset('images/app-create/personality/fetish.avif') }
];

export const firstNames = ['Yolanda', 'Ava', 'Mia', 'Luna', 'Sofia', 'Nina', 'Valeria'];
export const lastNames = ['Cross', 'Stone', 'Blake', 'Lane', 'Rose', 'Vale', 'Noir'];

export type QuizState = {
  gender: string;
  style: string;
  ethnicity: string;
  age: number;
  eyeColor: string;
  hairColor: string;
  hairStyle: string;
  bodyType: string;
  breastSize: string;
  buttSize: string;
  name: string;
  greeting: string;
  scenario: string;
};

export const initialQuizState: QuizState = {
  gender: 'female',
  style: 'realistic',
  ethnicity: 'asian',
  age: 24,
  eyeColor: 'blue',
  hairColor: 'brown',
  hairStyle: 'braided',
  bodyType: 'slim',
  breastSize: 'medium',
  buttSize: 'small',
  name: 'Yolanda Cross',
  greeting: '',
  scenario: ''
};
